import React, { useMemo, useState } from 'react';
import { Spin } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import HotelHeaderCard from './HotelHeaderCard';
import RoomOptionCard from './RoomOptionCard';
import CollapsibleResultGroup from './CollapsibleResultGroup';
import { formatBotPrice } from './formatBotPrice';

interface PackageRoomOptionsListProps {
  raw: any;
  onSelect: (selection: {
    room_selection_key: string;
    room_id: string | number;
    original_room_id?: string;
    adult: number;
    child: number;
    infant: number;
  }, friendlyText: string, pickLabel: string) => void;
  /** View-only mode: superseded list — collapsed + non-interactive. */
  disabled?: boolean;
  /** room_selection_key of the previously picked room (badged when disabled). */
  selectedKey?: string;
  /** Human label of the previous pick, shown in the collapsed header. */
  pickLabel?: string;
}

const PackageRoomOptionsList: React.FC<PackageRoomOptionsListProps> = ({
  raw,
  onSelect,
  disabled = false,
  selectedKey,
  pickLabel,
}) => {
  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const [selectingKey, setSelectingKey] = useState<string | null>(null);

  const packageInfo = raw?.packageInfo ?? raw;
  const currency: string = packageInfo?.currency_sign ?? '';
  const hotel = packageInfo?.hotel ?? {};
  const nights = packageInfo?.nights;

  const roomMeta = useMemo(() => {
    const map = new Map<string, any>();
    for (const r of packageInfo?.rooms ?? []) {
      if (r?.id != null) map.set(String(r.id), r);
    }
    return map;
  }, [packageInfo?.rooms]);

  const prices: any[] = Array.isArray(packageInfo?.price) ? packageInfo.price : [];
  if (!prices.length) return null;

  const handleSelect = (price: any) => {
    if (disabled) return;
    const roomKey = String(price.original_room_id ?? price.room_id);
    const selectionKey = `${roomKey}|${price.adult ?? 0}|${price.child ?? 0}|${price.infant ?? 0}`;
    setSelectingKey(selectionKey);
    const meta = roomMeta.get(String(price.room_id));
    const roomName = meta?.name ?? price.room_name ?? 'Room';
    const total = formatBotPrice(price.room_price ?? 0);
    const pax = [
      price.adult ? `${price.adult} adult${price.adult === 1 ? '' : 's'}` : null,
      price.child ? `${price.child} child${price.child === 1 ? '' : 'ren'}` : null,
      price.infant ? `${price.infant} infant${price.infant === 1 ? '' : 's'}` : null,
    ].filter(Boolean).join(', ');
    const parts = [roomName, pax, price.board, `${currency}${total}`].filter(Boolean);
    const friendly = `${t?.room?.bookLeadIn ?? "I'll book the"} ${parts.join(' · ')}`;
    onSelect(
      {
        room_selection_key: selectionKey,
        room_id: price.room_id,
        original_room_id: price.original_room_id,
        adult: price.adult ?? 0,
        child: price.child ?? 0,
        infant: price.infant ?? 0,
      },
      friendly,
      roomName,
    );
  };

  const list = (
    <div className="bot-search-results bot-room-options">
      <HotelHeaderCard
        name={hotel.name ?? packageInfo?.name ?? ''}
        stars={hotel.stars}
        destination={hotel.address ?? hotel.city}
        imageUrl={Array.isArray(hotel.images) ? hotel.images[0] : undefined}
      />
      {nights != null && (
        <div className="bot-result-card__meta bot-package-room-options__nights">
          🌙 {nights} {t?.result?.nights ?? 'nights'}
        </div>
      )}

      <div className="bot-search-results__list">
        {prices.map((price: any, i: number) => {
          const roomKey = `${price.original_room_id ?? price.room_id}|${price.adult ?? 0}|${price.child ?? 0}|${price.infant ?? 0}`;
          const meta = roomMeta.get(String(price.room_id));
          const isSelecting = selectingKey === roomKey;
          const isPicked = disabled && selectedKey === roomKey;
          return (
            <div
              key={`${roomKey}-${i}`}
              className={`bot-result-card-wrapper${
                isSelecting ? ' bot-result-card-wrapper--selecting' : ''
              }${isPicked ? ' bot-result-card-wrapper--picked' : ''}`}
            >
              {isPicked && (
                <span className="bot-result-card-wrapper__pick-badge">
                  {t?.collapsed?.yourPick ?? 'Your pick'}
                </span>
              )}
              <RoomOptionCard
                bookHash={roomKey}
                roomName={meta?.name ?? price.room_name ?? ''}
                board={price.board}
                totalPrice={price.room_price ?? 0}
                currency={currency}
                guests={`${price.adult ?? 0}A${price.child ? ` ${price.child}C` : ''}${price.infant ? ` ${price.infant}I` : ''}`}
                onSelect={() => handleSelect(price)}
              />
              {isSelecting && (
                <div
                  className="bot-result-card-wrapper__overlay"
                  role="status"
                  aria-live="polite"
                >
                  <Spin size="small" />
                  <span className="bot-result-card-wrapper__overlay-label">
                    {t?.addingToCart ?? 'Adding to cart…'}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  if (!disabled) return list;

  return (
    <CollapsibleResultGroup
      icon="🛏️"
      title={t?.collapsed?.title?.room ?? 'Room options'}
      count={prices.length}
      pickLabel={pickLabel}
    >
      {list}
    </CollapsibleResultGroup>
  );
};

export default PackageRoomOptionsList;
